// /ai, dedicated page describing the AI capabilities across the CoSec Global suite.
import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Brain, Wand2, Scan, GitBranch, Bot, ShieldCheck, ArrowUpRight, CheckCircle2, ArrowRight } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import FeatureTabs from "@/components/site/FeatureTabs";
import { Button } from "@/components/ui/button";
import StructuredData from "@/components/site/StructuredData";

const SITE_BASE = typeof window !== "undefined" ? window.location.origin : "";

const HERO_BADGES = ["GDPR-aligned", "EU/UK data residency", "Audit-ready prompts", "Human-in-the-loop"];

const CAPABILITIES = [
  {
    Icon: Brain,
    eyebrow: "CoSec App",
    title: "AI compliance copilot",
    body: "Drafts board minutes, prepares CS01 statements, summarises long filings and flags PSC changes before deadlines. Trained on UK Companies House rules and updated whenever the legislation moves.",
    bullets: ["Board-meeting transcription & action-tracking", "Confirmation-statement assistant", "Deadline-risk scoring across portfolios"],
  },
  {
    Icon: Scan,
    eyebrow: "CoForm + CoSec App",
    title: "Document understanding",
    body: "OCR + reasoning model that lifts shareholder, PSC, officer and address data straight out of PDFs, certificates and onboarding packs, and writes it back into the right Companies House fields.",
    bullets: ["Certificate of incorporation parsing", "Articles & shareholder agreements extraction", "Auto-mapping into statutory registers"],
  },
  {
    Icon: Wand2,
    eyebrow: "CoForm",
    title: "AI-assisted Companies House forms",
    body: "Tell CoForm what you want to file in plain English, &quot;appoint Jane as a director from 1 March&quot;. We translate it into a fully validated AP01 ready for one-click filing.",
    bullets: ["Natural-language form intent", "Automatic validation against CH schemas", "One-click filing with audit receipt"],
  },
  {
    Icon: GitBranch,
    eyebrow: "CoSearch",
    title: "Group-structure & ownership reasoning",
    body: "CoSearch reasons across 5m+ UK companies, directors and PSCs. Ask it &quot;show me every entity ultimately controlled by X&quot; and it traces the full beneficial-ownership graph in seconds.",
    bullets: ["Beneficial-ownership traversal", "Director-network detection", "Sector & sanctions cross-referencing"],
  },
  {
    Icon: Bot,
    eyebrow: "CoVerify",
    title: "ID verification with AI",
    body: "Connected to CoVerify. Combines biometric liveness, document tampering detection and AML screening. ECCTA-ready, all in under 60 seconds. The AI flags risk in real time.",
    bullets: ["Biometric liveness + face match", "Document forgery detection", "AML / sanctions / PEP screening"],
  },
  {
    Icon: ShieldCheck,
    eyebrow: "Across the suite",
    title: "Responsible & explainable",
    body: "Every AI decision has an audit trail. We never train on your data, we don't ship to non-EU/UK regions, and a human reviewer can override every output. Compliance teams stay in control.",
    bullets: ["No training on customer data", "EU/UK residency by default", "Per-decision explainability log"],
  },
];

export default function AiPage() {
  const SOFTWARE_LD = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "CoSec Global. AI-native compliance suite",
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Compliance & Governance",
    operatingSystem: "Web",
    description:
      "AI-native UK company-secretarial suite: AI drafting, document understanding, group-structure reasoning, ID verification and form generation.",
    url: `${SITE_BASE}/ai`,
    image: `${SITE_BASE}/favicon.ico`,
    provider: { "@type": "Organization", name: "CoSec Global", url: SITE_BASE },
    featureList: CAPABILITIES.map((c) => c.title),
    offers: { "@type": "Offer", priceCurrency: "GBP", price: "0", availability: "https://schema.org/OnlineOnly" },
    aggregateRating: undefined,
  };
  const BREADCRUMB_LD = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_BASE}/` },
      { "@type": "ListItem", position: 2, name: "AI", item: `${SITE_BASE}/ai` },
    ],
  };
  const FAQ_LD = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Do you train AI models on customer data?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "No. CoSec Global never trains on customer data. Every AI decision has an audit trail and a human reviewer can override every output.",
        },
      },
      {
        "@type": "Question",
        name: "Where is data hosted?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "All data is hosted in EU/UK regions on AWS by default, full GDPR-aligned residency.",
        },
      },
      {
        "@type": "Question",
        name: "Which products use AI?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Every product: CoSec App (drafting + compliance copilot), CoForm (natural-language Companies House filings), CoSearch (group-structure & ownership reasoning) and CoVerify (biometric + document AI for KYC).",
        },
      },
    ],
  };

  return (
    <SiteLayout>
      <StructuredData data={SOFTWARE_LD} id="ld-ai-software" />
      <StructuredData data={BREADCRUMB_LD} id="ld-ai-breadcrumb" />
      <StructuredData data={FAQ_LD} id="ld-ai-faq" />
      {/* Hero */}
      <section className="dark-hero relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-[0.08] pointer-events-none" />
        <div className="container-cv relative pt-16 md:pt-24 pb-12 max-w-5xl">
          <span className="pill"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI at CoSec Global</span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.04] tracking-tight">
            Built AI-native, not AI-bolted-on.{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-accent">From day one.</span>
              <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
            </span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-3xl">
            CoSec Global was designed from the ground up to put AI behind every filing, board pack, identity check and search.
            It&apos;s not a chatbot bolted onto a static portal, it&apos;s the productivity layer of an entire UK compliance suite.
          </p>
          <div className="mt-7 flex flex-wrap gap-2">
            {HERO_BADGES.map((b) => (
              <span key={b} className="light-surface inline-flex items-center gap-1.5 rounded-full border border-primary/15 bg-white text-primary text-xs font-bold px-3 py-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-accent" /> {b}
              </span>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-semibold">
              <Link to="/contact" data-testid="ai-book-demo">Book a live demo <ArrowUpRight className="ml-1 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-6 border-white/30 text-white hover:bg-white hover:text-[hsl(207_74%_21%)] font-semibold bg-white/[0.04]">
              <Link to="/coverify">Try CoVerify</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Capabilities, tabbed */}
      <div className="border-b border-border">
        <FeatureTabs
          testId="ai-capabilities"
          eyebrow="Where the AI lives"
          heading={<>Six places we use AI <span className="text-accent">every single day.</span></>}
          items={CAPABILITIES.map((c, i) => ({
            key: `${i}-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
            Icon: c.Icon,
            title: c.title,
            desc: c.body,
            bullets: c.bullets,
          }))}
        />
      </div>

      {/* Final CTA */}
      <section className="container-cv py-20 md:py-28">
        <div className="rounded-3xl border border-border bg-primary text-primary-foreground p-10 md:p-14 relative overflow-hidden">
          <div className="absolute inset-0 bg-dotgrid opacity-[0.08] pointer-events-none" />
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">
                See the AI in action.{" "}
                <span className="text-accent">In your own data.</span>
              </h2>
              <p className="mt-3 text-white/80 leading-relaxed max-w-xl">
                Bring 3–5 entities and we&apos;ll run them live through the suite, drafting a confirmation statement, building a group map and verifying an officer&apos;s ID. All inside the demo.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Button asChild className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-bold">
                <Link to="/contact" data-testid="ai-final-cta">Book the AI demo <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
